/* ResetPage auto-generated by npm run generator */
import { Button, CircularProgress, TextField, Typography } from "@material-ui/core"
import { usePasswordChecker } from "components/atoms/PasswordChecker"
import PortalForm, { PortalFormProps, PortalSubmitButton } from "components/molecules/PortalForm"
import * as firebase from "firebase/app"
import "firebase/auth"
import { observable } from "mobx"
import { observer } from "mobx-react"
import React from "react"
import { useHistory } from "react-router-dom"
import { PATHS } from "Router"

interface ResetPageProps {
    oobCode: string | null
}

enum Stage {
    LOADING,
    SEND_RESET,
    DETAILS,
    ERROR,
    LOGGED_IN,
}

class ResetPageState {
    @observable
    stage: Stage = Stage.LOADING

    @observable
    email = ""

    @observable
    linkSent?: boolean

    @observable
    error = ""
}

const ResetPage: React.FC<ResetPageProps> = (props) => {
    const state = React.useRef(new ResetPageState()).current
    const history = useHistory()
    const { passwordChecker, invalidPassword, password } = usePasswordChecker([state.email])

    const cancel = () => {
        state.stage = Stage.SEND_RESET
    }

    // check for oobCode on initial page load to determine if user is ready for password change
    // also sets email
    React.useEffect(() => {
        const verifyCode = async (code: string) => {
            try {
                const email = await firebase.auth().verifyPasswordResetCode(code)
                state.email = email
                state.stage = Stage.DETAILS
            } catch (e) {
                state.error = e.message
                state.stage = Stage.ERROR
            }
        }
        if (props.oobCode) {
            verifyCode(props.oobCode)
        } else {
            state.stage = Stage.SEND_RESET
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const render: PortalFormProps = {
        [Stage.LOADING]: {
            title: "Password Reset",
            subtitle: "",
            form: <></>,
            buttons: <CircularProgress />,
            buttonAction: async () => {},
        },
        [Stage.SEND_RESET]: {
            title: "Password Reset",
            subtitle: "Enter the email of the account you would like to reset",
            form: (
                <TextField
                    label="Email"
                    value={state.email}
                    onChange={(e) => (state.email = e.target.value)}
                    helperText="Account Email"
                    autoFocus
                    autoComplete="username"
                />
            ),
            buttons: (
                <>
                    {state.linkSent ? (
                        <Typography variant="h6">Please check your email for a sign in link</Typography>
                    ) : (
                        <PortalSubmitButton>Send Reset Link</PortalSubmitButton>
                    )}
                </>
            ),
            buttonAction: async () => {
                await firebase.auth().sendPasswordResetEmail(state.email)
                state.linkSent = true
            },
        },
        [Stage.DETAILS]: {
            title: "Enter new password",
            subtitle: "",
            form: (
                <>
                    <TextField label="Email" value={state.email} onChange={(e) => (state.email = e.target.value)} autoComplete="username" disabled />
                    {passwordChecker}
                </>
            ),
            buttons: (
                <>
                    <PortalSubmitButton disabled={invalidPassword}>Continue</PortalSubmitButton>
                    <Button onClick={cancel} variant="outlined" fullWidth>
                        Cancel
                    </Button>
                </>
            ),
            buttonAction: async () => {
                if (props.oobCode) {
                    await firebase.auth().confirmPasswordReset(props.oobCode, password)
                    state.stage = Stage.LOGGED_IN
                } else {
                    throw new Error("You came too far without a valid oobCode")
                }
            },
        },
        [Stage.ERROR]: {
            title: "Error",
            subtitle: state.error,
            form: <></>,
            buttons: (
                <Button onClick={cancel} variant="outlined" fullWidth>
                    Retry
                </Button>
            ),
            buttonAction: async () => {},
        },
        [Stage.LOGGED_IN]: {
            title: "Reset Complete",
            subtitle: "Please login with your new password",
            form: <></>,
            buttons: (
                <PortalSubmitButton type="button" onClick={() => history.push(PATHS.LOGIN())}>
                    Proceed to Login
                </PortalSubmitButton>
            ),
            buttonAction: async () => {},
        },
    }[state.stage]
    return <PortalForm title={render.title} subtitle={render.subtitle} form={render.form} buttons={render.buttons} buttonAction={render.buttonAction} />
}

export default observer(ResetPage)
